import { useState } from "react";

interface Props {
  value: string[];
  onChange?: (next: string[]) => void;
  /** Read-only mode (just display). */
  readOnly?: boolean;
  /** Size variant. */
  size?: "sm" | "md";
  /** Suggestions to offer in the autocomplete. */
  suggestions?: string[];
}

const palette = [
  "bg-rose-100 text-rose-800",
  "bg-amber-100 text-amber-800",
  "bg-emerald-100 text-emerald-800",
  "bg-sky-100 text-sky-800",
  "bg-violet-100 text-violet-800",
  "bg-pink-100 text-pink-800",
  "bg-teal-100 text-teal-800",
  "bg-orange-100 text-orange-800",
];

const colorFor = (tag: string) => {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) | 0;
  return palette[Math.abs(h) % palette.length]!;
};

export default function TagChips({ value, onChange, readOnly, size = "sm", suggestions }: Props) {
  const [draft, setDraft] = useState("");
  const cls = size === "sm" ? "px-2 py-0.5 text-xs" : "px-2.5 py-1 text-sm";

  const add = (raw: string) => {
    const t = raw.trim().toLowerCase().replace(/\s+/g, "-");
    if (!t || value.includes(t)) return;
    onChange?.([...value, t]);
    setDraft("");
  };
  const remove = (t: string) => onChange?.(value.filter((x) => x !== t));

  const matchedSuggestions = suggestions?.filter(
    (s) => s.includes(draft.toLowerCase()) && !value.includes(s),
  ) ?? [];

  return (
    <div className="flex flex-wrap items-center gap-1">
      {value.map((t) => (
        <span key={t} className={`pill ${colorFor(t)} ${cls} inline-flex items-center gap-1`}>
          {t}
          {!readOnly && (
            <button
              type="button"
              onClick={() => remove(t)}
              className="text-current opacity-50 hover:opacity-100"
              title="Remove tag"
            >
              ×
            </button>
          )}
        </span>
      ))}
      {!readOnly && (
        <span className="relative inline-block">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === ",") && draft.trim()) {
                e.preventDefault();
                add(draft);
              } else if (e.key === "Backspace" && !draft && value.length > 0) {
                onChange?.(value.slice(0, -1));
              }
            }}
            placeholder="+ tag"
            className="rounded-md border border-ink-200 bg-white px-2 py-0.5 text-xs w-20 focus:w-32 focus:outline-none focus:border-ink-700 transition-all"
          />
          {draft && matchedSuggestions.length > 0 && (
            <div className="absolute top-full left-0 mt-1 z-10 rounded-md border border-ink-200 bg-white shadow-md py-1 min-w-[120px]">
              {matchedSuggestions.slice(0, 5).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => add(s)}
                  className="block w-full text-left px-2 py-1 text-xs hover:bg-ink-100"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </div>
  );
}
